import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createBookings, getBookings } from "../../api/bookingsApi";
import PageLoader from "../../components/pageLoader";
import ToastContainer from "../../components/toastContainer";
import Toast from "../../components/toast";

const BookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const [showAddModal, setShowAddModal] = useState(false);
  const [creating, setCreating] = useState(false);

  const LIMIT = 10;

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const totalPages = Math.ceil(total / LIMIT);

  const fetchBookings = async () => {
    try {
      setLoading(true);

      const offset = (page - 1) * LIMIT;

      const data = await getBookings(LIMIT, offset);

      const list = Array.isArray(data) ? data : data.results || [];
      setTotal(data.count || 0);
      setBookings(list);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [page]);

  const formatAddress = (address) => {
    if (!address) return "-";

    const parts = [address.city, address.country];

    const filtered = parts.filter((p) => p && p.trim() !== "");

    return filtered.length ? filtered.join(", ") : "-";
  };

  const handleCreatePromoter = async (data) => {
    try {
      //setLoading(true);
      setCreating(true);

      const payload = {
        name: data.name,
        company_name: data.company_name,
        contact_name: data.contact_name,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,

        company_id: data.company_id,
        company_vat_no: data.company_vat_no,
        company_reg_no: data.company_reg_no,

        closest_airports: data.closestAirports.map((name) => ({ name })),

        address: {
          address_1: data.addressLine1,
          address_2: data.addressLine2,
          address_3: data.addressLine3,
          city: data.city,
          country: data.country,
          postcode: data.postcode,
        },

        venue: {
          name: data.venue_name,
          address: {
            address_1: data.venue_addressLine1,
            address_2: data.venue_addressLine2,
            address_3: data.venue_addressLine3,
            city: data.venue_city,
            county: data.venue_county,
            country: data.venue_country,
            postcode: data.venue_postcode,
          },
        },
      };

      await createBookings(payload);

      setToast({
        type: "success",
        message: "Promoter created successfully",
      });

      setShowAddModal(false);
      fetchBookings(); // refresh list
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">
            Bookings Management
          </h1>
          <p className="text-sm text-slate-400">Create & Manage Bookings</p>
        </div>

        <button
          onClick={() => navigate("/dashboard/bookings/create")}
          className="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-sm font-medium transition"
        >
          + Create Booking
        </button>
      </div>

      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-slate-400">
            <tr>
              <th className="px-6 py-3 text-center">Id</th>
              <th className="px-6 py-3 text-center">Promoter</th>
              <th className="px-6 py-3 text-center">Event Date</th>
              <th className="px-6 py-3 text-center">Town & Country</th>
              <th className="px-6 py-3 text-center">Artist</th>
              <th className="px-6 py-3 text-center">Contract Status</th>
              <th className="px-6 py-3 text-center">Payment Status</th>
              <th className="px-6 py-3 text-center">Booking Status</th>
            </tr>
          </thead>

          <tbody>
            {bookings.map((p) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/dashboard/bookings/${p.id}`)}
                className="border-b text-center border-white/5 hover:bg-slate-800/40 transition cursor-pointer"
              >
                <td className="px-6 py-4 text-white font-medium">{p.id}</td>
                <td className="px-6 py-4 text-slate-300">
                  {p.promoter?.company_name || "-"}
                </td>
                <td className="px-6 py-4 text-slate-300">{p.date || "-"}</td>
                <td className="px-6 py-4 text-slate-300">
                  {formatAddress(p.address) || "-"}
                </td>
                <td className="px-6 py-4 text-slate-300">
                  {p.artist?.stage_name || "-"}
                </td>
                <td className="px-6 py-4 text-slate-300">
                  {p.contract_status || "-"}
                </td>
                <td className="px-6 py-4 text-slate-300">
                  {p.payment_status || "-"}
                </td>
                <td className="px-6 py-4 text-green-400 font-medium">
                  {p.status ?? "-"}
                </td>
              </tr>
            ))}

            {!bookings.length && (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-10 text-center text-slate-400"
                >
                  No bookings found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex justify-end">
          {totalPages > 1 && (
            <div className="flex items-center gap-3 px-6 py-4 text-sm text-slate-400">
              {/* Previous */}
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
              >
                Previous
              </button>

              {/* Pages */}
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
                ${
                  page === pageNumber
                    ? "bg-pink-600 text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
                    >
                      {pageNumber}
                    </button>
                  ),
                )}
              </div>

              {/* Next */}
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === totalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* <PromoterFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreatePromoter}
        loading={false}
      /> */}

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default BookingsPage;
